import { Car, Phone, Shield, TrendingUp, FileText, Gavel } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";
import { useLanguage } from "@/hooks/useLanguage";
import { personalInjuryContent } from "@/i18n/personal-injury-content";

const AutoAccident = () => {
  const { language } = useLanguage();
  const content = personalInjuryContent.autoAccident[language];

  return (
    <GenericPracticePage
      title={content.title}
      subtitle={content.subtitle}
      icon={<Car className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">{content.sections[0].title}</h2>
            <p className="text-lg text-muted-foreground mb-4">
              {content.sections[0].content}
            </p>
            <p className="text-lg text-muted-foreground">
              Insurance companies often try to minimize payouts, but with experienced legal representation, you can level the playing field and get the settlement you deserve.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Auto Accidents We Handle</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Rear-End Collisions</h3>
                <p className="text-sm text-muted-foreground">Most common type, often causing whiplash and back injuries</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">T-Bone/Side Impact</h3>
                <p className="text-sm text-muted-foreground">Intersection crashes with serious injury potential</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Head-On Collisions</h3>
                <p className="text-sm text-muted-foreground">Severe crashes often causing catastrophic injuries</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Multi-Vehicle Pileups</h3>
                <p className="text-sm text-muted-foreground">Complex cases with multiple liable parties</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Hit and Run Accidents</h3>
                <p className="text-sm text-muted-foreground">Pursuing compensation through uninsured motorist coverage</p>
              </div>
              <div className="p-4 border rounded-lg">
                <h3 className="font-bold mb-2">Distracted Driving</h3>
                <p className="text-sm text-muted-foreground">Texting, phone use, or other negligent behavior</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common Auto Accident Injuries</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg space-y-3">
              <p className="text-muted-foreground">• <strong>Whiplash & Neck Injuries:</strong> Often delayed symptoms requiring extensive treatment</p>
              <p className="text-muted-foreground">• <strong>Back & Spinal Injuries:</strong> Including herniated discs and permanent disability</p>
              <p className="text-muted-foreground">• <strong>Traumatic Brain Injuries:</strong> Concussions and more severe head trauma</p>
              <p className="text-muted-foreground">• <strong>Broken Bones & Fractures:</strong> Requiring surgery and lengthy recovery</p>
              <p className="text-muted-foreground">• <strong>Soft Tissue Damage:</strong> Sprains, strains, and torn ligaments</p>
              <p className="text-muted-foreground">• <strong>Internal Injuries:</strong> Organ damage and internal bleeding</p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">What Compensation Can You Recover?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <FileText className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">Economic Damages</h3>
                <p className="text-sm text-muted-foreground">Medical bills, lost wages, property damage, future medical care</p>
              </div>
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">Non-Economic</h3>
                <p className="text-sm text-muted-foreground">Pain and suffering, emotional distress, loss of enjoyment</p>
              </div>
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-gradient-gold-shine flex items-center justify-center mx-auto mb-4 shadow-gold">
                  <Gavel className="h-8 w-8 text-primary" />
                </div>
                <h3 className="font-bold mb-2">Punitive Damages</h3>
                <p className="text-sm text-muted-foreground">In cases of gross negligence or intentional harm</p>
              </div>
            </div>
          </section>

          <section className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Why Act Quickly?</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Texas has a 2-year statute of limitations for injury claims</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Evidence disappears and witnesses' memories fade</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Insurance companies may pressure you into quick, low settlements</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">•</span>
                <span>Medical documentation must be preserved and organized</span>
              </li>
            </ul>
          </section>

          <section className="text-center bg-accent/5 p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Injured in an Auto Accident?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Don't wait. Contact us today for a case evaluation. We work on contingency - no fee unless we win.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default AutoAccident;
